<script setup lang="ts">
import type { GroupMember } from '~/models/Group'
import type { Form } from '~/models/Content/Form'
import type { CreateResource } from '~/models/Content/CreateResource'
import type { ContentModelText } from '~/models/Content/ContentModelText'
import type { ContentImage } from '~/models/Content/ContentImage'
import type { ContentFormWriter } from '~/models/Content/ContentFormWriter'
import type { ImageRelationField } from '~/models/Content/BaseField'
import type { AssignmentAnswer } from '~/models/Assignment'
import { useI18n } from 'vue-i18n'
import { computed, defineAsyncComponent, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useQuery } from '@tanstack/vue-query'
import { KsButton, KsDropdown } from '@aschehoug/kloss'
import { debounce } from '~/utils/functionUtils'
import useDialogStore from '~/stores/dialog'
import useAuthStore from '~/stores/auth'
import { PendoFeature } from '~/models/Pendo'
import { Subtree } from '~/models/Content/Subtree'
import { ArticleSize } from '~/models/Content/ContentArticle'
import { useMedia } from '~/composables/useMedia'
import useImage from '~/composables/useImage'
import useFormWriter from '~/composables/useFormWriter'
import useContentApi from '~/api/contentApi'
import FormWriterSkeleton from '~/components/skeletons/FormWriterSkeleton.vue'
import UploadedFile from '~/components/formwriter/components/UploadedFile.vue'
import ToolBar from '~/components/formwriter/components/ToolBar.vue'
import StudentFilters from '~/components/formwriter/components/StudentFilters.vue'
import InputField from '~/components/formwriter/components/InputField.vue'
import ImageUpload from '~/components/formwriter/components/ImageUpload.vue'
import ImagesBar from '~/components/formwriter/components/ImagesBar.vue'
import ModelText from '~/components/article/ModelText.vue'

const { item } = defineProps<{ item: ContentFormWriter }>()

const { t } = useI18n()
const { getImageSrc } = useImage()
const { findContents } = useContentApi()
const { userId, isTeacher, isStudent } = storeToRefs(useAuthStore())
const { showDialog } = useDialogStore()

const fields = ref<string[]>([])
const images = ref<string[]>([])
const currentImage = ref<string>('')
const showPlaceholder = ref(true)
const showDescription = ref(true)
const showLabel = ref(true)
const showModelText = ref(false)
const activeStudent = ref<GroupMember | null>(null)
const initialFormData = ref<{ image: string, fields: string[] } | null>(null)
const currentVersion = ref<AssignmentAnswer | null>(null)

const currentUserId = computed(() => {
  if (isTeacher.value && activeStudent.value) {
    return activeStudent.value.userId
  } else if (isTeacher.value && item.assignment) {
    return ''
  } else {
    return userId.value
  }
})

const {
  data,
  isLoading: isLoadingFields,
  isError
} = useQuery({
  queryKey: ['form_writer_fields', item.locationId, item.form.destinationContentId],
  queryFn: async () => (await findContents<Form>({
    contentIdCriterion: [Number(item.form.destinationContentId)],
    subtreeCriterion: [Subtree.Forms]
  }))[0],
  enabled: !!item.form.destinationContentId,
  staleTime: Infinity
})

const {
  data: modelText,
  isLoading: isLoadingModelText,
} = useQuery({
  queryKey: ['form_writer_model_text', item.locationId, item.modelText.destinationContentId],
  queryFn: async () => (await findContents<ContentModelText>({
    contentIdCriterion: [Number(item.modelText.destinationContentId)],
    subtreeCriterion: [Subtree.Media, Subtree.Content]
  }))[0],
  enabled: !!item.modelText.destinationContentId,
  staleTime: Infinity
})

const {
  data: imagesOnCanvas,
  isFetching
} = useMedia(item.imagesOnCanvas.destinationContentIds ?? [])

const {
  assignment,
  metadata,
  updateForm,
  saveAssignment,
  isLoadingFormData,
} = useFormWriter(item.locationId, currentUserId, item.assignment, item.persistent)

const isLoading = computed(() => isLoadingFields.value || isLoadingFormData.value || isLoadingModelText.value)
const showImageUpload = computed(() => item.imageUpload || item.imagesOnCanvas.destinationContentIds.length)
const currentFormData = computed(() => ({ image: currentImage.value, fields: fields.value }))
const versions = computed(() => {
  const answers = assignment.value?.answer || []
  if (!answers.length)
    return []

  // To avoid duplicate versions when students press the CTA button
  return answers.length > 1 && answers[0].data === answers[1].data
    ? answers.slice(1)
    : answers
})
const previousVersion = computed(() => assignment.value?.answer.find(({ isDraft }) => !isDraft))

const formData = computed(() => {
  if (!item.assignment && metadata.value) {
    return metadata.value
  }

  if (currentVersion.value) {
    return JSON.parse(currentVersion.value.data)
  }

  if (metadata.value && item.assignment) {
    if (!assignment.value || assignment.value?.answer.length === 0) {
      return metadata.value
    }
  }

  return null
})

const isFile = computed(() => Boolean(formData.value?.file))

const canCreateNewVersion = computed(() => {
  if (!item.assignment || !currentVersion.value || isLoadingFormData.value || saveAssignment.isPending.value)
    return false

  const hasNewChanges = JSON.stringify(currentFormData.value) !== JSON.stringify(initialFormData.value)
  const pendingChanges = JSON.stringify(currentVersion.value.data) !== JSON.stringify(previousVersion.value?.data)

  return pendingChanges !== null && pendingChanges !== undefined ? pendingChanges : hasNewChanges
})

const debouncedUpdateForm = debounce((formData: { fields: string[], image: string }) => updateForm(formData), 1000)

const onInput = (index: number, value: string) => {
  fields.value[index] = value
  debouncedUpdateForm(currentFormData.value)
}

const onSubmit = () => {
  if (!currentVersion.value)
    return

  saveAssignment.mutateAsync({
    ...currentVersion.value,
    data: JSON.stringify(currentFormData.value)
  })

  initialFormData.value = { ...currentFormData.value }
}

const openFileUploadDialog = async () => {
  const dialog = defineAsyncComponent(() => import('~/components/dialogs/FileUploadDialog.vue'))
  const uploadedFile = await showDialog<CreateResource>(dialog)

  if (uploadedFile && currentVersion.value) {
    await saveAssignment.mutateAsync({
      ...currentVersion.value,
      data: JSON.stringify({
        file: {
          title: uploadedFile.title,
          url: uploadedFile.url,
        }
      })
    })
  }
}

const deleteFile = async () => {
  if (!isFile.value || !currentVersion.value)
    return
  await saveAssignment.mutateAsync({
    ...currentVersion.value,
    data: JSON.stringify({
      image: '',
      fields: [],
    })
  })
  fields.value = []
  currentImage.value = ''
}

watch(activeStudent, (student) => {
  if (student && versions.value.length) {
    currentVersion.value = versions.value[0]
  } else {
    currentVersion.value = null
    fields.value = []
    currentImage.value = ''
  }
})

watch(versions, () => {
  if (!versions.value.length)
    return

  if ((isStudent.value) || (isTeacher.value && activeStudent.value)) {
    currentVersion.value = versions.value[0]
  }
}, { immediate: true })

watch(formData, (data) => {
  if (!data)
    return

  currentImage.value = data.image || ''

  if (!fields.value.length || (isTeacher.value && item.assignment)) {
    fields.value = [...(data.fields ?? [])]
  }

  if (isStudent.value) {
    initialFormData.value ??= { ...data }
  }
}, { immediate: true })

watch(imagesOnCanvas, () => {
  if (!imagesOnCanvas.value.length)
    return
  images.value = imagesOnCanvas.value.map(img =>
    getImageSrc((img as ContentImage).image as ImageRelationField, 'thumbnail_large'))
}, { immediate: true })

watch([isFetching, currentImage], () => {
  if (isFetching.value || !currentImage.value)
    return
  if (!images.value.includes(currentImage.value)) {
    images.value = [currentImage.value, ...images.value]
  }
})
</script>

<template>
  <div class="relative size-full space-y-2">
    <StudentFilters
      v-if="isTeacher && item.assignment"
      v-model="activeStudent"
      :item
    />
    <div class="relative mx-auto mt-24 w-full rounded-xl bg-[--theme-10] text-[--theme-60] md:mt-0 print:mt-0" :class="showModelText ? 'p-0' : 'p-4 sm:p-8'">
      <p v-if="isError" v-text="t('formWriter.error')" />
      <FormWriterSkeleton v-else-if="isLoading" />
      <template v-else-if="data?.fields.length">
        <ToolBar
          v-model:show-label="showLabel"
          v-model:show-description="showDescription"
          v-model:show-placeholder="showPlaceholder"
          v-model:show-model-text="showModelText"
          :item
          :fields
          :current-image
          :is-file
        />
        <template v-if="!showModelText">
          <ImagesBar
            v-if="isStudent && !isFetching && imagesOnCanvas.length && !isFile"
            v-model:images="images"
            v-model:current-image="currentImage"
            @update:current-image="updateForm(currentFormData)"
          />
          <div class="mb-4 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
            <span class="text-2xl font-extrabold" v-text="item.title" />
            <div v-if="assignment && isStudent && !isFile" class="flex items-center gap-3 print:hidden">
              <KsButton
                variant="secondary"
                :data-pendo="PendoFeature.FormWriter.UploadFileStudent"
                @click="openFileUploadDialog"
              >
                {{ t('formWriter.uploadFile') }}
              </KsButton>
              <KsButton
                form="form-writer-form"
                type="submit"
                variant="primary"
                :disabled="!canCreateNewVersion"
                :data-pendo="PendoFeature.FormWriter.DeliverStudent"
              >
                {{ !previousVersion ? t('formWriter.submit') : t('formWriter.submitNew') }}
              </KsButton>
            </div>
            <label v-if="isTeacher && activeStudent" class="flex w-1/3 flex-col gap-1 self-start print:!hidden">
              <span class="text-sm font-medium" v-text="t('formWriter.labels.chooseVersion')" />
              <KsDropdown
                v-model="currentVersion"
                :options="versions"
                :option-label="(answer: AssignmentAnswer) => answer.version"
                :placeholder="t('formWriter.labels.chooseVersion')"
                :no-options="t('formWriter.labels.noVersions')"
                :deselectable="() => false"
              />
            </label>
          </div>
          <UploadedFile
            v-if="isFile"
            :file="formData.file"
            @delete-file="deleteFile"
          />
          <ImageUpload
            v-if="showImageUpload && !isFile"
            v-model:images="images"
            v-model:current-image="currentImage"
            :item
            @update:current-image="updateForm(currentFormData)"
          />
          <transition-group
            v-if="!isFile"
            id="form-writer-form"
            appear
            tag="form"
            class="flex flex-col gap-5"
            enter-active-class="duration-500 ease delay-[calc(0.1s+(0.1s*var(--i)))]"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            @submit.prevent.stop="onSubmit"
          >
            <InputField
              v-for="(field, index) in data.fields"
              :key="field.id"
              v-model="fields[index]"
              :field
              :show-label
              :show-description
              :show-placeholder
              :disable-fields="isTeacher && item.assignment"
              :style="{ '--i': index }"
              @input="onInput(index, $event.target.value)"
            />
            <KsButton
              v-if="assignment && isStudent && data.fields.length >= 3"
              type="submit"
              variant="primary"
              :disabled="!canCreateNewVersion"
              class="self-end"
              :data-pendo="PendoFeature.FormWriter.DeliverStudent"
            >
              {{ !previousVersion ? t('formWriter.submit') : t('formWriter.submitNew') }}
            </KsButton>
          </transition-group>
        </template>
        <ModelText
          v-if="showModelText && modelText"
          :content="modelText"
          :size="ArticleSize.Medium"
          class="prose prose-lg !bg-[--theme-5] first:prose-headings:mt-0"
        />
      </template>
    </div>
  </div>
</template>

<style scoped>
:deep(.ks-button) {
  --ks-roundness: 0.5;
  padding: 0.9rem !important;
}

:deep(.ks-button-primary) {
  --ks-primary: var(--theme-50);
  --ks-secondary: var(--theme-40);
  --ks-primarytext: var(--theme-5);
}

:deep(.ks-button-secondary) {
  --ks-input: var(--theme-5);
  --ks-secondary: var(--theme-60);
  --ks-inputhover: var(--theme-10);
  border: 2px solid var(--theme-50) !important;
}

:deep(.ks-dropdown) {
  --ks-roundness: 0.5;
  --ks-input: var(--theme-5);
  --ks-secondary: var(--theme-60);
  --ks-inputhover: var(--theme-20);
  --ks-dropdownhover: var(--theme-10);
}
</style>
